import { useEffect, useRef, useState } from 'react';

import type { Item, PaginatedGenerator, Props, UseSelectPanelFieldReturn } from './types';

const DEFAULT_PAGE_SIZE = 25;

const useSelectPanelField = <T>({
    dependency,
    dependencyRequiredText,
    fetchFn,
    noItemsFoundText = '',
    pageSize = DEFAULT_PAGE_SIZE,
    parseFn,
    resetFn
}: Props<T>): UseSelectPanelFieldReturn => {
    const [items, setItems] = useState<Item[]>([]);
    const [noItemsText, setNoItemsText] = useState('');
    const [hasNextPage, setHasNextPage] = useState(false);

    const generatorRef = useRef<PaginatedGenerator<T> | null>(null);

    const fetchData = async (request?: { name?: string; pageSize?: number }) => {
        if (dependencyRequiredText && !dependency) {
            setNoItemsText(dependencyRequiredText);
            setItems([]);
            setHasNextPage(false);

            return;
        }

        resetFn();
        generatorRef.current = fetchFn({ ...request, pageSize });
        setItems([]);

        const { done, value } = await generatorRef.current.next();

        if (!value || value.length === 0) {
            setNoItemsText(noItemsFoundText);
            setHasNextPage(false);

            return;
        }

        setNoItemsText('');
        setItems(parseFn(value));
        setHasNextPage(!done);
    };

    const onNextPage = async (): Promise<void> => {
        if (!generatorRef.current || !hasNextPage) {
            return;
        }

        const result = await generatorRef.current.next();

        if (result.value) {
            const parsedNewItems = parseFn(result.value as T[]);

            setItems(current => [...current, ...parsedNewItems]);
        }

        setHasNextPage(!result.done);
    };

    const handleAsyncFilter = async (searchTerm: string) => {
        await fetchData(searchTerm ? { name: searchTerm } : undefined);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dependency]);

    return {
        handleAsyncFilter,
        hasNextPage,
        items,
        noItemsText,
        onNextPage
    };
};

export default useSelectPanelField;
